"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useRef } from "react"
import { CustomBreadcrumbs, Desktop, Mobile } from "ui"
import Container from "ui/Container"
import { replaceRichTextLinks } from "utils/replaceRichTextLinks"
import CopyButton from "../Common/CopyButton"
import SingleJobForm from "./SingleJobForm"

const StyledMain = styled.div`
  padding-top: 48px;
  padding-bottom: 64px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 64px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 381px;
  }
`

const StyledTextContent = styled.div`
  padding-top: 48px;
`

const StyledRightSideDiv = styled.div``

const StyledLeftSideDiv = styled.div``

const StyledCopyWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding-top: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    > div {
      max-width: 60%;
    }
  }
`

const StyledScrollButton = styled(Button)`
  background-color: #008761;
`

const StyledLoaderDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 493px;
  > div {
    width: 70px;
    height: 70px;
  }
`
const StyledDescription = styled(Typography)<{ component?: string }>`
  span {
    padding: 0 !important;
    min-height: 1.2em;
  }
`

type Props = {
  jobId: string
  jobData: any
}

const SingleJob = ({ jobId, jobData }: Props) => {
  // Decodes HTML entities - works on both server and client
  const decodeHTML = (html: string) => {
    const htmlEntities: { [key: string]: string } = {
      "&amp;": "&",
      "&lt;": "<",
      "&gt;": ">",
      "&quot;": '"',
      "&#x27;": "'",
      "&#x2F;": "/",
      "&#x60;": "`",
      "&#x3D;": "=",
      "&nbsp;": " ",
      "&copy;": "©",
      "&reg;": "®",
      "&trade;": "™",
    }

    return html.replace(/&[#\w]+;/g, (entity) => {
      return htmlEntities[entity] || entity
    })
  }

  const formRef = useRef<HTMLDivElement>(null)

  const scrollToForm = () => {
    if (formRef.current) {
      const offset = 100
      const y =
        formRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        offset
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  const breadCrumbs = {
    selfObject: { title: jobData?.jobTitle, url: "/career/headquarters" },

    parents: [
      {
        title: "ראשי",
        url: "/",
      },
      {
        title: "קריירה באגד",
        url: "/career/headquarters",
      },
    ],
  }

  // Use the current path for a canonical shareable link
  const link =
    typeof window !== "undefined"
      ? `${window.location.origin}/career/headquarters/${jobId}`
      : ""

  return (
    <Container paddingMobile>
      <CustomBreadcrumbs breadcrumbs={breadCrumbs} />

      <StyledMain>
        <StyledRightSideDiv>
          {jobData && <Typography variant="h2">{jobData?.jobTitle}</Typography>}
          <StyledCopyWrapper>
            <Desktop>
              <Typography variant="body1">
                {" "}
                <span style={{ fontWeight: 700 }}>תחום: </span>
                {jobData?.categoryName}
              </Typography>
            </Desktop>

            <Mobile>
              <StyledScrollButton variant="contained" onClick={scrollToForm}>
                הגשת מועמדות
              </StyledScrollButton>
            </Mobile>
            <CopyButton link={link} />
          </StyledCopyWrapper>
          <StyledTextContent>
            <Typography
              variant="body1"
              style={{ fontWeight: 700, paddingBottom: "24px" }}
            >
              {" "}
              תיאור המשרה
            </Typography>
            <StyledDescription
              variant="body1"
              component={"div"}
              style={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={
                jobData?.description
                  ? {
                      __html: replaceRichTextLinks(
                        decodeHTML(jobData.description),
                      ),
                    }
                  : { __html: "" }
              }
            />

            <Typography
              variant="body1"
              style={{ fontWeight: 700, padding: "24px 0" }}
            >
              {" "}
              דרישות התפקיד
            </Typography>
            <StyledDescription
              variant="body1"
              component={"div"}
              style={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={
                jobData?.requirements
                  ? {
                      __html: replaceRichTextLinks(
                        decodeHTML(jobData.requirements),
                      ),
                    }
                  : { __html: "" }
              }
            />

            <Typography
              variant="body1"
              style={{ fontWeight: 700, padding: "24px 0" }}
            >
              {" "}
              מיומניות
            </Typography>
            <Typography
              variant="body1"
              component={"div"}
              style={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={
                jobData?.skills
                  ? { __html: replaceRichTextLinks(decodeHTML(jobData.skills)) }
                  : { __html: "" }
              }
            />
          </StyledTextContent>
        </StyledRightSideDiv>

        <StyledLeftSideDiv>
          <SingleJobForm jobId={jobId} formRef={formRef} />
        </StyledLeftSideDiv>
      </StyledMain>
    </Container>
  )
}

export default SingleJob
